/**
 * Merchant Member List Page
 *
 * Comprehensive member management interface for merchant admins.
 * Provides member listing, filtering, role management, and member removal capabilities.
 *
 * Features:
 * - Member list with filtering
 * - Role-based filtering (admin, staff, member)
 * - Status filtering (active, disabled)
 * - Member removal functionality
 * - Responsive design with mobile support
 */

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useMerchantMembers, useMemberDetail, useMerchantGroups } from "@/hooks-v2/aggregator";
import { useMerchantMemberActions } from "@/hooks-v2/coordinator";
import { useCurrentMerchant } from "@/hooks-v2/context";
// REMOVED: InviteMemberModal - inviteMember feature removed from codebase
// Members now join via self-registration using merchant registration link (Story 3.6)
import MemberAnalyticsModal from "../../components/merchant/MemberAnalyticsModal";
import AddMemberModal from "../../components/merchant/AddMemberModal";
import ManageGroupsPanel from "../../components/merchant/ManageGroupsPanel";
import type { MerchantMember } from "@/types/common.types";
import { TimestampService, formatMoney } from "@zlinkx/shared-v2";
import type { GetMembersParams } from "@/types/ui.types";
import { useTranslation } from "@/i18n";
import { formatForDisplay } from "@zlinkx/shared-v2";

// Note: Filter options are now dynamically created in the component using translations

// Sort Icon Component
const SortIcon: React.FC<{ active?: boolean; direction?: "asc" | "desc" }> = ({
  active,
  direction,
}) => {
  if (active !== true) {
    return (
      <svg
        className="w-4 h-4 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
        />
      </svg>
    );
  }

  if (direction === "asc") {
    return (
      <svg
        className="w-4 h-4 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 11l5-5m0 0l5 5m-5-5v12"
        />
      </svg>
    );
  }

  return (
    <svg
      className="w-4 h-4 text-blue-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 13l-5 5m0 0l-5-5m5 5V6"
      />
    </svg>
  );
};

// Format number with thousand separators
const formatNumber = (value: number): string => {
  return new Intl.NumberFormat("en-US").format(value);
};

const getMemberElapsedMilliseconds = (
  timestamp: Date | string | null | undefined,
): number => {
  if (timestamp === null || timestamp === undefined) {
    return Number.POSITIVE_INFINITY;
  }

  if (typeof timestamp === "string" && timestamp.length === 0) {
    return Number.POSITIVE_INFINITY;
  }

  const parsed =
    timestamp instanceof Date
      ? timestamp
      : TimestampService.parseDateTimeInput(timestamp);
  if (!TimestampService.isValidDate(parsed)) {
    return Number.POSITIVE_INFINITY;
  }

  const elapsed = TimestampService.getMillisecondsSince(parsed);
  return Number.isFinite(elapsed) ? elapsed : Number.POSITIVE_INFINITY;
};

const MemberList: React.FC = () => {
  const { t } = useTranslation();

  const {
    selectedMerchant,
    selectedMerchantId,
    loading: isLoadingMerchants,
  } = useCurrentMerchant();
  const memberActions = useMerchantMemberActions();
  const groupsData = useMerchantGroups(
    selectedMerchantId ?? 0,
    selectedMerchantId !== null,
  );

  // Filter options for member management
  const ROLE_FILTERS = useMemo(
    () =>
      [
        { value: "all", label: t("merchant.memberList.filters.allRoles") },
        { value: "admin", label: t("merchant.memberList.filters.admins") },
        { value: "staff", label: t("merchant.memberList.filters.staff") },
        { value: "member", label: t("merchant.memberList.filters.members") },
      ] as const,
    [t],
  );

  const STATUS_FILTERS = useMemo(
    () =>
      [
        { value: "all", label: t("merchant.memberList.filters.allStatus") },
        { value: "true", label: t("merchant.memberList.filters.active") },
        { value: "false", label: t("merchant.memberList.filters.disabled") },
      ] as const,
    [t],
  );

  // Local filter state
  const [filters, setFilters] = useState<
    GetMembersParams & {
      sortBy: "name" | "role" | "joined" | "credit" | "points";
      sortOrder: "asc" | "desc";
      search: string;
    }
  >({
    role: "all",
    isActive: "all",
    sortBy: "joined",
    sortOrder: "desc",
    search: "",
  });

  // Debounced search state for performance
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search ?? "");
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // REMOVED: Invite member modal state - inviteMember feature removed
  // Members now join via self-registration using merchant registration link

  // Analytics modal state
  const [selectedMember, setSelectedMember] = useState<MerchantMember | null>(
    null,
  );
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isManageGroupsOpen, setIsManageGroupsOpen] = useState(false);

  // Fetch individual member detail for the analytics modal
  const memberDetail = useMemberDetail({
    merchantId: selectedMerchantId ?? 0,
    userId: selectedMember ? String(selectedMember.userId) : null,
    enabled: selectedMerchantId !== null && selectedMember !== null,
  });

  // Use members aggregator
  const membersData = useMerchantMembers({
    merchantId: selectedMerchantId ?? 0,
    enabled: selectedMerchantId !== null,
  });

  // Extract data
  const allMembers = membersData.data.members;
  const isLoading = membersData.isLoading || isLoadingMerchants;
  const error = membersData.error;

  // Pagination from the aggregator (backed by cursor-based useInfiniteQuery)
  const hasNextPage = membersData.hasNextPage;
  const fetchNextPage = membersData.fetchNextPage;
  const isFetchingNextPage = membersData.isFetchingNextPage;

  // Apply frontend filtering
  const filteredMembers = useMemo(() => {
    let result = [...allMembers];

    // Apply role filter
    if (filters.role !== undefined && filters.role !== "all") {
      result = result.filter((member) => {
        return member.role === filters.role;
      });
    }

    // Apply status filter
    if (filters.isActive !== "all") {
      const targetStatus = filters.isActive === true;
      result = result.filter((member) => {
        return (member.status === "active") === targetStatus;
      });
    }

    // Apply search filter (using debounced value for performance)
    if (debouncedSearch.trim().length > 0) {
      const searchLower = debouncedSearch.toLowerCase().trim();
      result = result.filter((member) => {
        const displayName = (member.displayName ?? "").toLowerCase();
        const phoneMasked = (
          member.phoneMasked ??
          member.phone_masked ??
          ""
        ).toLowerCase();
        return (
          displayName.includes(searchLower) || phoneMasked.includes(searchLower)
        );
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let compareValue = 0;

      switch (filters.sortBy) {
        case "name":
          compareValue = (a.displayName ?? "").localeCompare(
            b.displayName ?? "",
          );
          break;
        case "role":
          compareValue = (a.role ?? "").localeCompare(b.role ?? "");
          break;
        case "joined": {
          const elapsedA = getMemberElapsedMilliseconds(a.createdAt);
          const elapsedB = getMemberElapsedMilliseconds(b.createdAt);
          compareValue = elapsedB - elapsedA;
          break;
        }
        case "credit":
          compareValue = (a.creditBalance ?? 0) - (b.creditBalance ?? 0);
          break;
        case "points":
          compareValue = (a.pointsBalance ?? 0) - (b.pointsBalance ?? 0);
          break;
        default:
          compareValue = 0;
      }

      return filters.sortOrder === "desc" ? -compareValue : compareValue;
    });

    return result;
  }, [allMembers, filters, debouncedSearch]);

  // Total count of filtered members (for display)
  const totalItems = filteredMembers.length;

  // Handle filter changes
  const handleFilterChange = useCallback(
    (
      newFilters: Partial<
        GetMembersParams & {
          sortBy: "name" | "role" | "joined" | "credit" | "points";
          sortOrder: "asc" | "desc";
          search: string;
        }
      >,
    ) => {
      const updatedFilters = { ...filters, ...newFilters };
      setFilters(updatedFilters);
    },
    [filters],
  );

  // Handle sorting changes
  const handleSort = useCallback(
    (sortBy: "name" | "role" | "joined" | "credit" | "points") => {
      const newSortOrder =
        filters.sortBy === sortBy && filters.sortOrder === "asc"
          ? "desc"
          : "asc";
      setFilters((prev) => ({ ...prev, sortBy, sortOrder: newSortOrder }));
    },
    [filters.sortBy, filters.sortOrder],
  );

  // REMOVED: Handle invite member modal - inviteMember feature removed
  // Members now join via self-registration using merchant registration link

  // Handle analytics modal
  const handleOpenAnalyticsModal = useCallback((member: MerchantMember) => {
    setSelectedMember(member);
    setIsAnalyticsModalOpen(true);
  }, []);

  const handleCloseAnalyticsModal = useCallback(() => {
    setIsAnalyticsModalOpen(false);
    setSelectedMember(null);
  }, []);

  // Format member role for display
  const formatRole = (role: string): string => {
    switch (role) {
      case "admin":
        return t("merchant.memberList.roles.admin");
      case "staff":
        return t("merchant.memberList.roles.staff");
      case "member":
        return t("merchant.memberList.roles.member");
      default:
        return role;
    }
  };

  // Format member status for display
  const formatStatus = (isActive: boolean): string => {
    return isActive
      ? t("merchant.memberList.filters.active")
      : t("merchant.memberList.filters.disabled");
  };

  // Format date for display
  const formatDate = (timestamp: string | Date | null): string => {
    try {
      if (
        timestamp === null ||
        (typeof timestamp === "string" && timestamp.length === 0)
      ) {
        return "—";
      }
      // Handle both string timestamps and Date objects
      const date =
        timestamp instanceof Date
          ? timestamp
          : TimestampService.parseDateTimeInput(timestamp);
      if (!TimestampService.isValidDate(date)) {
        return "—";
      }
      // Use centralized formatting from shared-v2
      return formatForDisplay(date, { style: "short" });
    } catch {
      return "—";
    }
  };

  // Loading state
  if (isLoadingMerchants) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {t("merchant.memberList.loadingMerchant")}
          </p>
        </div>
      </div>
    );
  }

  if (selectedMerchant === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t("merchant.memberList.noMerchantTitle")}
          </h2>
          <p className="text-gray-600">
            {t("merchant.memberList.noMerchantMessage")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {t("merchant.memberList.title")}
            </h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              {t("merchant.memberList.subtitle", {
                merchantName: selectedMerchant.merchantName,
              })}
            </p>
          </div>
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              onClick={() => setIsAddMemberModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base"
            >
              {t("merchant.memberList.addMember")}
            </button>
            <button
              type="button"
              onClick={() => setIsManageGroupsOpen(value => !value)}
              disabled={isLoading}
              className="rounded-lg border border-blue-600 px-4 py-2 font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-50 text-sm sm:text-base"
            >
              {t("merchant.memberList.groups.manage")}
            </button>
            <button
              onClick={() => window.location.reload()}
              disabled={isLoading}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base"
            >
              {isLoading
                ? t("merchant.memberList.refreshing")
                : t("merchant.memberList.refresh")}
            </button>
          </div>
        </div>
      </div>

      {isManageGroupsOpen && (
        <ManageGroupsPanel
          merchantId={selectedMerchantId ?? 0}
          members={allMembers}
          groups={groupsData.groups}
          isLoading={groupsData.isLoading}
          error={groupsData.error}
          onRefresh={groupsData.refetch}
          onCreate={data => memberActions.createGroup(selectedMerchantId ?? 0, data)}
          onUpdate={(cardId, data) => memberActions.updateGroup(selectedMerchantId ?? 0, cardId, data)}
          onAssign={(cardId, userId) => memberActions.assignGroupMember(selectedMerchantId ?? 0, cardId, userId)}
          onRemove={(cardId, userId) => memberActions.removeGroupMember(selectedMerchantId ?? 0, cardId, userId)}
        />
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
          {t("merchant.memberList.filters.title")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Role Filter */}
          <div>
            <label
              htmlFor="role-filter"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t("merchant.memberList.filters.role")}
            </label>
            <select
              id="role-filter"
              value={filters.role || "all"}
              onChange={(e) =>
                handleFilterChange({
                  role: e.target.value as GetMembersParams["role"],
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            >
              {ROLE_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label
              htmlFor="status-filter"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t("merchant.memberList.filters.status")}
            </label>
            <select
              id="status-filter"
              value={
                filters.isActive === true
                  ? "true"
                  : filters.isActive === false
                    ? "false"
                    : "all"
              }
              onChange={(e) => {
                const value = e.target.value;
                const isActiveValue =
                  value === "true" ? true : value === "false" ? false : "all";
                handleFilterChange({ isActive: isActiveValue });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search Filter */}
          <div>
            <label
              htmlFor="search-filter"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t("merchant.memberList.filters.search")}
            </label>
            <div className="relative">
              <input
                id="search-filter"
                type="text"
                value={filters.search ?? ""}
                onChange={(e) => handleFilterChange({ search: e.target.value })}
                placeholder={t("merchant.memberList.filters.searchPlaceholder")}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
              />
              {filters.search.length > 0 && (
                <button
                  type="button"
                  onClick={() => handleFilterChange({ search: "" })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error !== null && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t("merchant.memberList.errorLoadingMembers")}
              </h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            {t("merchant.memberList.totalMembers", { total: totalItems })}
          </h2>
        </div>

        {isLoading ? (
          <div className="p-6 sm:p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">
              {t("merchant.memberList.loadingMembers")}
            </p>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="p-8 sm:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <p className="text-gray-600 text-sm sm:text-base font-medium">
              {t("merchant.memberList.noMembersFound")}
            </p>
            <p className="text-gray-500 text-xs sm:text-sm mt-2">
              {t("merchant.memberList.emptyStateHint")}
            </p>
            <a
              href="/merchant/settings#registration"
              className="inline-flex items-center mt-4 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {t("merchant.memberList.emptyStateLink")}
              <svg
                className="w-4 h-4 ml-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </a>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort("name")}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>{t("merchant.memberList.table.member")}</span>
                        <SortIcon
                          active={filters.sortBy === "name"}
                          direction={
                            filters.sortBy === "name"
                              ? filters.sortOrder
                              : undefined
                          }
                        />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort("role")}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>{t("merchant.memberList.table.role")}</span>
                        <SortIcon
                          active={filters.sortBy === "role"}
                          direction={
                            filters.sortBy === "role"
                              ? filters.sortOrder
                              : undefined
                          }
                        />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("merchant.memberList.table.status")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("merchant.memberList.table.phone")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort("credit")}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>
                          {t("merchant.memberList.table.creditBalance")}
                        </span>
                        <SortIcon
                          active={filters.sortBy === "credit"}
                          direction={
                            filters.sortBy === "credit"
                              ? filters.sortOrder
                              : undefined
                          }
                        />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort("points")}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>
                          {t("merchant.memberList.table.pointsBalance")}
                        </span>
                        <SortIcon
                          active={filters.sortBy === "points"}
                          direction={
                            filters.sortBy === "points"
                              ? filters.sortOrder
                              : undefined
                          }
                        />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort("joined")}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>{t("merchant.memberList.table.joined")}</span>
                        <SortIcon
                          active={filters.sortBy === "joined"}
                          direction={
                            filters.sortBy === "joined"
                              ? filters.sortOrder
                              : undefined
                          }
                        />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredMembers.map((member) => {
                    // Fall back to phone if display name is empty
                    const displayName =
                      member.displayName ??
                      member.phoneMasked ??
                      member.phone_masked ??
                      "Unknown";
                    const nameInitial =
                      displayName.charAt(0) === "+"
                        ? displayName.charAt(1) || "?"
                        : displayName.charAt(0);
                    return (
                      <tr
                        key={member.userId}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleOpenAnalyticsModal(member)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                                <span className="text-white font-medium">
                                  {nameInitial.toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {displayName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {t("merchant.memberList.table.idPrefix")}
                                {member.userId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              member.role === "admin"
                                ? "bg-purple-100 text-purple-800"
                                : member.role === "staff"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {formatRole(member.role)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              member.status === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {formatStatus(member.status === "active")}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {member.phoneMasked ?? member.phone_masked ?? "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatMoney(
                            member.creditBalance ?? member.credit_balance ?? 0,
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(
                            member.pointsBalance ?? member.points_balance ?? 0,
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(member.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filteredMembers.map((member) => {
                // Fall back to phone if display name is empty
                const displayName =
                  member.displayName ??
                  member.phoneMasked ??
                  member.phone_masked ??
                  "Unknown";
                const nameInitial =
                  displayName.charAt(0) === "+"
                    ? displayName.charAt(1) || "?"
                    : displayName.charAt(0);
                return (
                  <div
                    key={member.userId}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start space-x-3">
                      {/* Avatar */}
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {nameInitial.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Member Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {displayName}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                              {t("merchant.memberList.table.idPrefix")}
                              {member.userId}
                            </p>
                          </div>
                        </div>

                        {/* Badges and Info */}
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              member.role === "admin"
                                ? "bg-purple-100 text-purple-800"
                                : member.role === "staff"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {formatRole(member.role)}
                          </span>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              member.status === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {formatStatus(member.status === "active")}
                          </span>
                        </div>

                        {/* Phone number */}
                        <p className="text-xs text-gray-500 mt-2">
                          {member.phoneMasked ?? member.phone_masked ?? "—"}
                        </p>

                        {/* Credit and Points Summary */}
                        <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500">
                              {t("merchant.memberList.mobile.credit")}
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatMoney(
                                member.creditBalance ??
                                  member.credit_balance ??
                                  0,
                              )}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500">
                              {t("merchant.memberList.mobile.points")}
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatNumber(
                                member.pointsBalance ??
                                  member.points_balance ??
                                  0,
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Join Date */}
                        <div className="mt-3 flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            {t("merchant.memberList.joinedPrefix")}
                            {formatDate(member.createdAt)}
                          </p>
                          <button
                            onClick={() => handleOpenAnalyticsModal(member)}
                            className="text-xs text-blue-600 hover:text-blue-900 font-medium"
                          >
                            {t("merchant.memberList.table.viewDetails")}{" "}
                            {t("merchant.memberList.viewDetailsArrow")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More */}
            {hasNextPage && filteredMembers.length > 0 && (
              <div className="p-4 sm:p-6 border-t border-gray-200 text-center">
                <button
                  onClick={fetchNextPage}
                  disabled={isFetchingNextPage}
                  className="inline-flex items-center px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isFetchingNextPage
                    ? t("common.loading")
                    : t("merchant.memberList.loadMore")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <AddMemberModal
        key={selectedMerchantId ?? 0}
        merchantId={selectedMerchantId ?? 0}
        groups={groupsData.groups}
        isOpen={isAddMemberModalOpen}
        onClose={() => setIsAddMemberModalOpen(false)}
        isSubmitting={memberActions.isLoading.enrollMember}
        onEnroll={(data) =>
          memberActions.enrollMember(selectedMerchantId ?? 0, data)
        }
      />

      {/* Member Analytics Modal */}
      <MemberAnalyticsModal
        userId={selectedMember ? String(selectedMember.userId) : null}
        merchantId={selectedMerchantId ?? 0}
        isOpen={isAnalyticsModalOpen}
        onClose={handleCloseAnalyticsModal}
        member={memberDetail.member}
        isLoading={memberDetail.isLoading}
        error={memberDetail.error}
        onRefetch={() => {
          void memberDetail.refetch();
        }}
      />
    </div>
  );
};

export default MemberList;
